import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Plus, Trash2, FileText, Upload, Download, Info, Copy, Loader2, Save, Pencil,
  FolderPlus, Folder, FolderOpen, ChevronRight, ChevronDown, GripVertical, MoveRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { plantillasWordAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionPlantillasWord = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

  const [plantillas, setPlantillas] = useState([]);
  const [carpetas, setCarpetas] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showVariablesDialog, setShowVariablesDialog] = useState(false);
  const [showCarpetaDialog, setShowCarpetaDialog] = useState(false);
  const [showMoverDialog, setShowMoverDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [editingCarpeta, setEditingCarpeta] = useState(null);
  const [moviendoPlantilla, setMoviendoPlantilla] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  const [newForm, setNewForm] = useState({ nombre: '', descripcion: '', carpeta_id: '' });
  const [newFile, setNewFile] = useState(null);
  const [carpetaForm, setCarpetaForm] = useState({ nombre: '', descripcion: '' });
  const [moverCarpetaId, setMoverCarpetaId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plantillasRes, carpetasRes, variablesRes] = await Promise.all([
        plantillasWordAPI.getAll(),
        plantillasWordAPI.getCarpetas(),
        plantillasWordAPI.getVariables()
      ]);
      setPlantillas(plantillasRes.data);
      setCarpetas(carpetasRes.data);
      setVariables(variablesRes.data.variables);
      // Expandir todas las carpetas por defecto
      const expanded = {};
      carpetasRes.data.forEach(c => { expanded[c.id] = true; });
      setExpandedFolders(prev => {
        const merged = { ...expanded };
        Object.keys(prev).forEach(k => { merged[k] = prev[k]; });
        return merged;
      });
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: "Error", description: "No se pudieron cargar las plantillas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (id) => {
    setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ===== PLANTILLAS CRUD =====
  const handleCrear = async () => {
    if (!newForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    if (!editingPlantilla && !newFile) {
      toast({ title: "Error", description: "Debes seleccionar un archivo .docx", variant: "destructive" });
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('nombre', newForm.nombre);
      formData.append('descripcion', newForm.descripcion || '');
      formData.append('carpeta_id', newForm.carpeta_id || '');
      if (newFile) {
        formData.append('archivo', newFile);
      }

      if (editingPlantilla) {
        await plantillasWordAPI.actualizar(editingPlantilla.id, formData);
        toast({ title: "Plantilla actualizada correctamente" });
      } else {
        await plantillasWordAPI.crear(formData);
        toast({ title: "Plantilla creada correctamente" });
      }
      setShowNewDialog(false);
      setNewForm({ nombre: '', descripcion: '', carpeta_id: '' });
      setNewFile(null);
      setEditingPlantilla(null);
      loadData();
    } catch (error) {
      console.error('Error guardando plantilla:', error);
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo guardar la plantilla", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const openEditar = (plantilla) => {
    setEditingPlantilla(plantilla);
    setNewForm({
      nombre: plantilla.nombre,
      descripcion: plantilla.descripcion || '',
      carpeta_id: plantilla.carpeta_id ? String(plantilla.carpeta_id) : ''
    });
    setNewFile(null);
    setShowNewDialog(true);
  };

  const openNueva = (carpetaId = null) => {
    setEditingPlantilla(null);
    setNewForm({ nombre: '', descripcion: '', carpeta_id: carpetaId ? String(carpetaId) : '' });
    setNewFile(null);
    setShowNewDialog(true);
  };

  const handleEliminar = async (id, nombre) => {
    if (!window.confirm(`Eliminar la plantilla "${nombre}"?`)) return;
    try {
      await plantillasWordAPI.eliminar(id);
      toast({ title: "Plantilla eliminada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  const handleDescargar = async (id, nombre) => {
    try {
      const response = await plantillasWordAPI.descargar(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre || 'plantilla.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo descargar", variant: "destructive" });
    }
  };

  // ===== CARPETAS CRUD =====
  const handleGuardarCarpeta = async () => {
    if (!carpetaForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('nombre', carpetaForm.nombre);
      formData.append('descripcion', carpetaForm.descripcion || '');

      if (editingCarpeta) {
        await plantillasWordAPI.actualizarCarpeta(editingCarpeta.id, formData);
        toast({ title: "Carpeta actualizada" });
      } else {
        await plantillasWordAPI.crearCarpeta(formData);
        toast({ title: "Carpeta creada" });
      }
      setShowCarpetaDialog(false);
      setCarpetaForm({ nombre: '', descripcion: '' });
      setEditingCarpeta(null);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo guardar la carpeta", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const openEditarCarpeta = (carpeta) => {
    setEditingCarpeta(carpeta);
    setCarpetaForm({ nombre: carpeta.nombre, descripcion: carpeta.descripcion || '' });
    setShowCarpetaDialog(true);
  };

  const openNuevaCarpeta = () => {
    setEditingCarpeta(null);
    setCarpetaForm({ nombre: '', descripcion: '' });
    setShowCarpetaDialog(true);
  };

  const handleEliminarCarpeta = async (id, nombre) => {
    const plantillasEnCarpeta = plantillas.filter(p => p.carpeta_id === id).length;
    const msg = plantillasEnCarpeta > 0
      ? `Eliminar la carpeta "${nombre}"? Las ${plantillasEnCarpeta} plantilla(s) que contiene pasaran a "Sin carpeta".`
      : `Eliminar la carpeta "${nombre}"?`;
    if (!window.confirm(msg)) return;
    try {
      await plantillasWordAPI.eliminarCarpeta(id);
      toast({ title: "Carpeta eliminada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar la carpeta", variant: "destructive" });
    }
  };

  // ===== MOVER PLANTILLA =====
  const openMover = (plantilla) => {
    setMoviendoPlantilla(plantilla);
    setMoverCarpetaId(plantilla.carpeta_id ? String(plantilla.carpeta_id) : '');
    setShowMoverDialog(true);
  };

  const handleMover = async () => {
    if (!moviendoPlantilla) return;
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('carpeta_id', moverCarpetaId || '');
      await plantillasWordAPI.actualizar(moviendoPlantilla.id, formData);
      toast({ title: `Plantilla movida correctamente` });
      setShowMoverDialog(false);
      setMoviendoPlantilla(null);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo mover la plantilla", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const copiarVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast({ title: "Copiado", description: `${variable} copiado al portapapeles` });
  };

  // ===== RENDER HELPERS =====
  const plantillasSinCarpeta = plantillas.filter(p => !p.carpeta_id);

  const renderPlantillaCard = (p) => (
    <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow" data-testid={`plantilla-card-${p.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-blue-600" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate">{p.nombre}</p>
          {p.descripcion && <p className="text-xs text-gray-500 truncate">{p.descripcion}</p>}
          <Badge variant="outline" className="text-xs mt-0.5">{p.archivo_nombre}</Badge>
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0 ml-2">
        <Button variant="ghost" size="sm" onClick={() => openMover(p)} title="Mover a carpeta" data-testid={`btn-mover-${p.id}`}>
          <MoveRight className="w-4 h-4 text-gray-500" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => openEditar(p)} title="Editar" data-testid={`btn-editar-${p.id}`}>
          <Pencil className="w-4 h-4 text-gray-500" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleDescargar(p.id, p.archivo_nombre)} title="Descargar" data-testid={`btn-descargar-${p.id}`}>
          <Download className="w-4 h-4 text-gray-500" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleEliminar(p.id, p.nombre)} title="Eliminar" className="text-red-500 hover:text-red-700" data-testid={`btn-eliminar-${p.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-gray-500">
        Solo los administradores pueden gestionar plantillas Word.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/configuracion')} data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Plantillas Word Judiciales
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Organiza tus plantillas .docx en carpetas para generar escritos judiciales
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowVariablesDialog(true)} data-testid="btn-ver-variables">
            <Info className="w-4 h-4 mr-2" />Variables
          </Button>
          <Button variant="outline" onClick={openNuevaCarpeta} data-testid="btn-nueva-carpeta">
            <FolderPlus className="w-4 h-4 mr-2" />Nueva carpeta
          </Button>
          <Button onClick={() => openNueva()} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nueva-plantilla">
            <Plus className="w-4 h-4 mr-2" />Nueva plantilla
          </Button>
        </div>
      </div>

      {/* Info box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Como funciona:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Crea <strong>carpetas</strong> para organizar tus plantillas por tipo de procedimiento</li>
                <li>Sube documentos Word (.docx) con variables como <code className="bg-blue-100 px-1 rounded">{'{{CLIENTE_NOMBRE}}'}</code></li>
                <li>Desde cualquier expediente, pulsa <strong>"GENERAR ESCRITO"</strong> y navega por carpetas o busca</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contenido principal */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : carpetas.length === 0 && plantillas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Folder className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No hay carpetas ni plantillas configuradas</p>
            <p className="text-sm text-gray-400 mb-4">Crea una carpeta y sube tu primera plantilla</p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={openNuevaCarpeta}>
                <FolderPlus className="w-4 h-4 mr-2" />Crear carpeta
              </Button>
              <Button onClick={() => openNueva()} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Subir plantilla
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Carpetas */}
          {carpetas.map(carpeta => {
            const plantillasEnCarpeta = plantillas.filter(p => p.carpeta_id === carpeta.id);
            const isExpanded = expandedFolders[carpeta.id];
            return (
              <Card key={`carpeta-${carpeta.id}`} className="overflow-hidden" data-testid={`carpeta-card-${carpeta.id}`}>
                <div
                  className="flex items-center justify-between p-3 bg-amber-50 border-b cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => toggleFolder(carpeta.id)}
                  data-testid={`carpeta-header-${carpeta.id}`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-amber-600" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-amber-600" />
                    )}
                    {isExpanded ? (
                      <FolderOpen className="w-5 h-5 text-amber-500" />
                    ) : (
                      <Folder className="w-5 h-5 text-amber-500" />
                    )}
                    <div>
                      <span className="font-semibold text-gray-900">{carpeta.nombre}</span>
                      {carpeta.descripcion && (
                        <span className="text-xs text-gray-500 ml-2">- {carpeta.descripcion}</span>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs ml-2">
                      {plantillasEnCarpeta.length} plantilla{plantillasEnCarpeta.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openNueva(carpeta.id)} title="Añadir plantilla a esta carpeta" data-testid={`btn-add-to-carpeta-${carpeta.id}`}>
                      <Plus className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditarCarpeta(carpeta)} title="Editar carpeta" data-testid={`btn-editar-carpeta-${carpeta.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEliminarCarpeta(carpeta.id, carpeta.nombre)} title="Eliminar carpeta" className="text-red-500 hover:text-red-700" data-testid={`btn-eliminar-carpeta-${carpeta.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {isExpanded && (
                  <CardContent className="p-3 space-y-2">
                    {plantillasEnCarpeta.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">
                        Carpeta vacia. Pulsa <strong>+</strong> para añadir plantillas.
                      </p>
                    ) : (
                      plantillasEnCarpeta.map(renderPlantillaCard)
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Plantillas sin carpeta */}
          {plantillasSinCarpeta.length > 0 && (
            <Card data-testid="sin-carpeta-section">
              <div className="flex items-center gap-2 p-3 bg-gray-50 border-b">
                <FileText className="w-5 h-5 text-gray-400" />
                <span className="font-semibold text-gray-600">Sin carpeta</span>
                <Badge variant="secondary" className="text-xs">
                  {plantillasSinCarpeta.length} plantilla{plantillasSinCarpeta.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <CardContent className="p-3 space-y-2">
                {plantillasSinCarpeta.map(renderPlantillaCard)}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dialog: Nueva/Editar plantilla */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingPlantilla ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingPlantilla ? 'Editar plantilla Word' : 'Nueva plantilla Word'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newForm.nombre}
                onChange={(e) => setNewForm({ ...newForm, nombre: e.target.value })}
                placeholder="Ej: Escrito comparecencia Juicio Verbal"
                data-testid="input-nombre-plantilla"
              />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={newForm.descripcion}
                onChange={(e) => setNewForm({ ...newForm, descripcion: e.target.value })}
                placeholder="Descripcion opcional de la plantilla..."
                rows={2}
                data-testid="input-descripcion-plantilla"
              />
            </div>
            <div>
              <Label>Carpeta</Label>
              <Select
                value={newForm.carpeta_id || 'none'}
                onValueChange={(v) => setNewForm({ ...newForm, carpeta_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="select-carpeta-plantilla">
                  <SelectValue placeholder="Sin carpeta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin carpeta</SelectItem>
                  {carpetas.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="flex items-center gap-2">
                        <Folder className="w-3.5 h-3.5 text-amber-500" />
                        {c.nombre}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Archivo Word (.docx) {editingPlantilla ? '(dejar vacio para mantener el actual)' : '*'}</Label>
              <div className="mt-1">
                <label className="flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {newFile ? newFile.name : editingPlantilla ? `Actual: ${editingPlantilla.archivo_nombre}` : 'Seleccionar archivo .docx'}
                  </span>
                  <input
                    type="file"
                    accept=".docx"
                    className="hidden"
                    onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                    data-testid="input-archivo-plantilla"
                  />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewDialog(false); setEditingPlantilla(null); }}>Cancelar</Button>
            <Button onClick={handleCrear} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar-plantilla">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingPlantilla ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nueva/Editar carpeta */}
      <Dialog open={showCarpetaDialog} onOpenChange={setShowCarpetaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingCarpeta ? <Pencil className="w-5 h-5" /> : <FolderPlus className="w-5 h-5 text-amber-500" />}
              {editingCarpeta ? 'Editar carpeta' : 'Nueva carpeta'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la carpeta *</Label>
              <Input
                value={carpetaForm.nombre}
                onChange={(e) => setCarpetaForm({ ...carpetaForm, nombre: e.target.value })}
                placeholder="Ej: Civil, Penal, Laboral..."
                data-testid="input-nombre-carpeta"
              />
            </div>
            <div>
              <Label>Descripcion (opcional)</Label>
              <Input
                value={carpetaForm.descripcion}
                onChange={(e) => setCarpetaForm({ ...carpetaForm, descripcion: e.target.value })}
                placeholder="Descripcion de la carpeta..."
                data-testid="input-descripcion-carpeta"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCarpetaDialog(false); setEditingCarpeta(null); }}>Cancelar</Button>
            <Button onClick={handleGuardarCarpeta} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="btn-guardar-carpeta">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingCarpeta ? 'Guardar cambios' : 'Crear carpeta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Mover plantilla */}
      <Dialog open={showMoverDialog} onOpenChange={setShowMoverDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MoveRight className="w-5 h-5" />
              Mover plantilla
            </DialogTitle>
          </DialogHeader>
          {moviendoPlantilla && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Mover <strong>"{moviendoPlantilla.nombre}"</strong> a:
              </p>
              <Select
                value={moverCarpetaId || 'none'}
                onValueChange={(v) => setMoverCarpetaId(v === 'none' ? '' : v)}
              >
                <SelectTrigger data-testid="select-mover-carpeta">
                  <SelectValue placeholder="Seleccionar carpeta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin carpeta</SelectItem>
                  {carpetas.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="flex items-center gap-2">
                        <Folder className="w-3.5 h-3.5 text-amber-500" />
                        {c.nombre}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoverDialog(false)}>Cancelar</Button>
            <Button onClick={handleMover} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirmar-mover">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MoveRight className="w-4 h-4 mr-2" />}
              Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Variables disponibles */}
      <Dialog open={showVariablesDialog} onOpenChange={setShowVariablesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5" />Variables disponibles para plantillas
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-4">
            Usa estas variables en tus documentos Word. Al generar el escrito, se reemplazaran automaticamente con los datos del expediente.
          </p>
          <div className="space-y-2">
            {variables.map((v, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 group">
                <code className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono min-w-[220px]">
                  {v.variable}
                </code>
                <span className="text-sm text-gray-600 flex-1">{v.descripcion}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => copiarVariable(v.variable)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionPlantillasWord;
